import React, { useRef, useState, useEffect } from "react";
import {
  Upload,
  Button,
  Table,
  Tooltip,
  Modal,
  InputNumber,
  Switch,
  message,
  Popover,
  Badge,
  Empty,
  Space,
} from "antd";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  FaUpload,
  FaTrash,
  FaFolderOpen,
  FaCog,
  FaDownload,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
  FaTimesCircle,
  FaImages,
  FaEye,
  FaInfoCircle,
  FaUndo,
} from "react-icons/fa";
import CompareImage from "react-compare-image";
import "bootstrap/dist/css/bootstrap.min.css";

const { Dragger } = Upload;

const SUPPORTED_FORMATS = [
  "image/jpeg",
  "image/png",
  "image/bmp",
  "image/gif",
  "image/tiff",
];
const SUPPORTED_FORMATS_LABEL = "JPG, PNG, BMP, GIF, TIFF";
const DEFAULT_QUALITY = 0.9;
const DEFAULT_MAX_SIZE = 30;

function getWebpName(originalName) {
  return originalName.replace(/\.[^/.]+$/, "") + ".webp";
}

function getExtFromType(type) {
  switch (type) {
    case "image/jpeg": return ".jpg";
    case "image/png": return ".png";
    case "image/bmp": return ".bmp";
    case "image/gif": return ".gif";
    case "image/tiff": return ".tiff";
    default: return "";
  }
}

async function imageFileToWebP(file, quality = DEFAULT_QUALITY, resize = null) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      try {
        let width = img.width, height = img.height;
        if (resize && (width > resize || height > resize)) {
          if (width > height) {
            height = Math.round((resize / width) * height);
            width = resize;
          } else {
            width = Math.round((resize / height) * width);
            height = resize;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject("WebP conversion failed.");
              return;
            }
            resolve(blob);
          },
          "image/webp",
          quality
        );
      } catch (err) {
        reject("Conversion error: " + err.message);
      }
    };
    img.onerror = () => reject("Cannot load image file.");
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => reject("File reading failed.");
    reader.readAsDataURL(file);
  });
}

export default function ImageWebPConverter() {
  const [fileList, setFileList] = useState([]);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [qualityModal, setQualityModal] = useState(false);
  const [desiredQuality, setDesiredQuality] = useState(DEFAULT_QUALITY);
  const [maxSize, setMaxSize] = useState(DEFAULT_MAX_SIZE);
  const [qualityPerImage, setQualityPerImage] = useState(false);
  const [resizeMax, setResizeMax] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);

  const [resetToken, setResetToken] = useState(0);
  const resetTokenRef = useRef(resetToken);
  useEffect(() => { resetTokenRef.current = resetToken; }, [resetToken]);
  const folderInputRef = useRef();

  // ---- UNIQUE BY FILENAME: replace old with new, never allow duplicate name ----
  const handleFiles = async (filesArr) => {
    const arr = Array.from(filesArr);

    const webpFiles = arr.filter(
      f => f.type === "image/webp" || f.name.toLowerCase().endsWith(".webp")
    );
    if (webpFiles.length > 0) {
      message.error(
        "WebP files are not supported as input. This tool converts TO WebP, not FROM WebP."
      );
    }

    const filteredArr = arr.filter((f) => SUPPORTED_FORMATS.includes(f.type));
    if (filteredArr.length < arr.length - webpFiles.length) {
      message.error(
        `Some files were skipped (not supported). Only ${SUPPORTED_FORMATS_LABEL} images are allowed.`
      );
    }
    if (filteredArr.length === 0) return;

    // Remove previous files with same name as any in filteredArr (replace on upload)
    const newNames = new Set(filteredArr.map(f => f.name));
    let newList = fileList.filter(f => !newNames.has(f.name));
    const queue = filteredArr
      .filter((f) => f.size > 0 && !f.name.startsWith("."))
      .map((file) => ({
        uid: `${Date.now()}_${Math.random()}`,
        name: file.name,
        origExt: getExtFromType(file.type),
        size: file.size,
        type: file.type,
        file,
        status: "queued",
        error: null,
        warning: null,
      }));

    setFileList([...newList, ...queue]);
    setTimeout(() => convertQueuedFiles([...newList, ...queue], resetTokenRef.current), 200);
    message.success(`${queue.length} image(s) added!`);
  };

  const processFile = async (
    file,
    userQuality = DEFAULT_QUALITY,
    userMaxSize = DEFAULT_MAX_SIZE,
    resizeMaxPx = null
  ) => {
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      return { error: "Unsupported file format" };
    }
    let tryQuality = userQuality;
    let attempts = 0;
    let blob = null;
    let lastSize = null;
    let warning = null;
    let minQuality = 0.2;
    while (attempts < 7 && tryQuality >= minQuality) {
      try {
        blob = await imageFileToWebP(file, tryQuality, resizeMaxPx);
        lastSize = blob.size;
        if (lastSize / 1024 <= userMaxSize || tryQuality <= minQuality) {
          break;
        }
        tryQuality -= 0.15;
        attempts++;
      } catch (e) {
        return { error: "Conversion failed: " + e };
      }
    }
    if (!blob) {
      return { error: "Failed to convert (canvas error or too large?)" };
    }
    if (lastSize / 1024 > userMaxSize) {
      warning = `Final file is ${(lastSize / 1024).toFixed(
        1
      )} KB (over ${userMaxSize}KB)`;
    }
    return {
      blob,
      size: lastSize,
      quality: tryQuality,
      warning,
    };
  };

  // ---- convertQueuedFiles with strict token check before every state update! ----
  const convertQueuedFiles = async (currentList, token) => {
    let updatedList = [...currentList];
    let updatedConverted = [];

    for (let idx = 0; idx < updatedList.length; idx++) {
      if (token !== resetTokenRef.current) return;
      let fileObj = updatedList[idx];
      if (fileObj.status === "done" || fileObj.status === "converting") continue;

      updatedList[idx] = { ...fileObj, status: "converting", error: null };
      if (token !== resetTokenRef.current) return;
      setFileList([...updatedList]);
      let result = await processFile(
        fileObj.file,
        qualityPerImage ? desiredQuality : DEFAULT_QUALITY,
        maxSize,
        resizeMax
      );

      if (token !== resetTokenRef.current) return;

      if (result.error) {
        updatedList[idx] = {
          ...fileObj,
          status: "failed",
          error: result.error,
        };
        if (token !== resetTokenRef.current) return;
        setFileList([...updatedList]);
        message.error(`Failed: ${fileObj.name}`);
      } else {
        let newName = getWebpName(fileObj.name);
        updatedList[idx] = {
          ...fileObj,
          status: "done",
          compressedBlob: result.blob,
          compressedUrl: URL.createObjectURL(result.blob),
          compressedSize: result.size,
          compressedName: newName,
          compressionQuality: result.quality,
          warning: result.warning,
        };
        updatedConverted.push(updatedList[idx]);
        if (token !== resetTokenRef.current) return;
        setFileList([...updatedList]);
        setConvertedFiles([...updatedConverted]);
      }
    }
  };

  const columns = [
    {
      title: "",
      width: 26,
      render: (v, record) => (
        <Badge
          status={
            record.status === "done"
              ? "success"
              : record.status === "converting"
                ? "processing"
                : record.status === "failed"
                  ? "error"
                  : "default"
          }
        />
      ),
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
    {
      title: "Preview",
      dataIndex: "file",
      width: 72,
      render: (file, record) =>
        SUPPORTED_FORMATS.includes(record.type) ? (
          <img
            src={URL.createObjectURL(file)}
            alt="preview"
            style={{
              width: 50,
              height: 50,
              objectFit: "cover",
              borderRadius: 10,
              border: "1px solid #eee",
              cursor: "pointer",
              transition: "box-shadow 0.2s",
              boxShadow: "0 1px 8px rgba(0,0,0,0.09)",
            }}
            onClick={() => {
              if (record.status === "done") {
                setPreviewRecord(record);
                setPreviewModal(true);
              }
            }}
          />
        ) : (
          <FaImages size={28} color="#bbb" />
        ),
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
    {
      title: "Filename",
      dataIndex: "name",
      render: (name, record) => (
        <span style={{ fontWeight: 500 }}>
          {name}
          {record.compressedName && (
            <div style={{ fontSize: 11, color: "#aaa" }}>
              → {record.compressedName}
            </div>
          )}
        </span>
      ),
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
    {
      title: "Original",
      dataIndex: "size",
      render: (size) => <span>{(size / 1024).toFixed(1)} KB</span>,
      sorter: (a, b) => a.size - b.size,
      responsive: ["sm", "md", "lg", "xl"],
    },
    {
      title: "WebP",
      dataIndex: "compressedSize",
      render: (size, record) =>
        record.status === "done" ? (
          <span>
            {(size / 1024).toFixed(1)} KB
            {record.warning && (
              <Tooltip title={record.warning}>
                <FaExclamationCircle
                  style={{ color: "#faad14", marginLeft: 5 }}
                />
              </Tooltip>
            )}
          </span>
        ) : (
          "-"
        ),
      sorter: (a, b) => (a.compressedSize || 0) - (b.compressedSize || 0),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, rec) =>
        status === "done" ? (
          <Tooltip title={rec.warning ? rec.warning : "Done"}>
            <span
              className={rec.warning ? "text-warning" : "text-success"}
              style={{ cursor: rec.warning ? "pointer" : "default" }}>
              <FaCheckCircle style={{ color: "#52c41a", marginRight: 4 }} />
              {rec.warning ? "Done (large)" : "Done"}
            </span>
          </Tooltip>
        ) : status === "converting" ? (
          <span style={{ color: "#faad14" }}>
            <FaSpinner className="fa-spin" style={{ marginRight: 4 }} />
            Converting...
          </span>
        ) : status === "failed" ? (
          <Tooltip title={rec.error || "Conversion failed"}>
            <span style={{ color: "#ff4d4f" }}>
              <FaTimesCircle style={{ marginRight: 4 }} /> Failed
            </span>
          </Tooltip>
        ) : (
          <span className="text-muted">Queued</span>
        ),
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
    {
      title: "",
      render: (v, rec) =>
        rec.status === "done" ? (
          <Tooltip title="Download">
            <Button
              icon={<FaDownload />}
              size="small"
              onClick={() =>
                saveAs(
                  rec.compressedBlob,
                  rec.compressedName || rec.name.replace(/\.[^/.]+$/, ".webp")
                )
              }
            />
          </Tooltip>
        ) : (
          <Button size="small" disabled />
        ),
      width: 50,
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "",
      render: (v, rec) =>
        rec.status === "done" ? (
          <Tooltip title="Preview">
            <Button
              icon={<FaEye />}
              size="small"
              onClick={() => {
                setPreviewRecord(rec);
                setPreviewModal(true);
              }}
            />
          </Tooltip>
        ) : (
          <Button size="small" disabled />
        ),
      width: 50,
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "",
      render: (v, rec) => (
        <Tooltip title="Remove">
          <Button
            icon={<FaTrash />}
            danger
            size="small"
            onClick={() => {
              setFileList((l) => l.filter((f) => f.uid !== rec.uid));
              setConvertedFiles((l) => l.filter((f) => f.uid !== rec.uid));
            }}
          />
        </Tooltip>
      ),
      width: 50,
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
  ];

  return (
    <div
      className="py-5"
      style={{
        background: "linear-gradient(120deg, #e4f0fb 0%, #ffffff 100%)",
        minHeight: "100vh",
      }}>
      <div className="container" style={{ maxWidth: 980 }}>
        {/* Upload & Actions */}
        <div
          className="bg-white rounded-5 shadow-lg p-4"
          style={{ marginBottom: 36 }}>
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-2 gap-2">
            <div className="d-flex align-items-center gap-2">
              <FaImages
                style={{ fontSize: 30, color: "#42a5f5", marginRight: 6 }}
              />
              <h2
                className="mb-0"
                style={{ fontWeight: 800, letterSpacing: -1.5, fontSize: 28 }}>
                WebP Bulk Image Converter
              </h2>
              <Popover
                content={
                  <div style={{ maxWidth: 340, fontSize: 15 }}>
                    <b>How to use:</b>
                    <ul className="mb-0 ps-3" style={{ fontSize: 14 }}>
                      <li>
                        Drop or select {SUPPORTED_FORMATS_LABEL} images or
                        folders
                      </li>
                      <li>
                        Set <b>quality</b> and <b>max file size</b> in{" "}
                        <FaCog style={{ marginBottom: -2 }} /> <b>Settings</b>
                      </li>
                      <li>
                        Preview <b>before/after</b> any image
                      </li>
                      <li>
                        Download individually or as <b>ZIP</b>
                      </li>
                    </ul>
                    <hr className="mt-2 mb-1" />
                    <div style={{ fontSize: 13, color: "#888" }}>
                      <FaInfoCircle /> No images ever leave your browser.
                      <br />
                      <FaInfoCircle /> Low file size may cause visible blur.
                      <br />
                    </div>
                  </div>
                }
                title="How to use"
                placement="bottomRight">
                <Button
                  icon={<FaInfoCircle />}
                  type="text"
                  size="small"
                  style={{ marginLeft: 7, color: "#2196f3" }}
                />
              </Popover>
            </div>
            <Button
              type="primary"
              shape="round"
              icon={<FaCog />}
              style={{ fontWeight: 600, letterSpacing: 0.2 }}
              onClick={() => setQualityModal(true)}>
              Settings
            </Button>
          </div>
          <div className="mb-3 text-muted" style={{ fontSize: 16 }}>
            Ultra-fast, fully in-browser. Convert images to WebP with perfect
            filenames, preview, and instant download.
          </div>
          {/* Main Actions */}
          <div className="w-100 mb-4 table-responsive-custom">
            <Dragger
              multiple
              showUploadList={false}
              accept={SUPPORTED_FORMATS.join(",")}
              beforeUpload={() => false}
              customRequest={() => { }}
              onDrop={(e) => {
                handleFiles(Array.from(e.dataTransfer.files));
              }}
              onChange={(info) => {
                // Only handle click selection (not drag), avoid double add
                if (info.fileList && info.fileList.length > 0) {
                  handleFiles(
                    info.fileList.map((f) => f.originFileObj).filter(Boolean)
                  );
                }
              }}
              style={{
                minWidth: 210,
                borderColor: "#42a5f5",
                background: "#f6fbff",
                borderRadius: 14,
                height: 90,
              }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}>
                <FaUpload size={28} color="#42a5f5" />
                <span
                  style={{
                    fontWeight: 500,
                    marginTop: 7,
                    color: "#222",
                    fontSize: 16,
                  }}>
                  Drop or click
                </span>
                <span style={{ fontSize: 13, color: "#888" }}>
                  Any number. Folders OK! <br /> <b>Only {SUPPORTED_FORMATS_LABEL}</b>
                </span>
              </div>
            </Dragger>
          </div>
          <Space wrap className="mb-2" size={[16, 10]}>
            <Button
              icon={<FaUpload />}
              type="primary"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = SUPPORTED_FORMATS.join(",");
                input.onchange = (e) => handleFiles(e.target.files);
                input.click();
              }}>
              Upload Single Image
            </Button>
            <Button
              icon={<FaImages />}
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = SUPPORTED_FORMATS.join(",");
                input.multiple = true;
                input.onchange = (e) => handleFiles(e.target.files);
                input.click();
              }}>
              Upload Multiple Images
            </Button>
            <Button
              icon={<FaFolderOpen />}
              onClick={() => {
                folderInputRef.current.click();
              }}>
              Upload Folder
            </Button>
            <input
              ref={folderInputRef}
              type="file"
              accept={SUPPORTED_FORMATS.join(",")}
              style={{ display: "none" }}
              multiple
              webkitdirectory="true"
              directory="true"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </Space>
          <div className="w-100 text-end">
            <Button
              icon={<FaUndo />}
              onClick={() => {
                window.location.reload();
                // setFileList([]);
                // setConvertedFiles([]);
                // setResetToken(t => t + 1); // Cancels all old async!
                message.info("Cleared all images.");
              }}
              danger>
              Clear All Images
            </Button>
          </div>
        </div>
        {/* File Table */}
        <div className="bg-white rounded-4 shadow-sm p-4 mb-4">
          <div className="d-flex align-items-center mb-3 gap-2">
            <FaImages style={{ color: "#1565c0" }} />
            <h5 className="mb-0">Uploaded Images</h5>
            <span className="text-muted" style={{ fontSize: 13 }}>
              ({fileList.length || 0})
            </span>
          </div>
          <div className="table-responsive-custom">
            {fileList.length === 0 ? (
              <Empty
                description="No images uploaded yet. Add images to start converting!"
                imageStyle={{ height: 80 }}
              />
            ) : (
              <Table
                columns={columns}
                dataSource={fileList}
                pagination={false}
                rowKey="uid"
                size="small"
                className="mb-4"
                bordered
                style={{ background: "#fff", borderRadius: 10, minWidth: 600 }}
                scroll={{ x: 650 }}
              />
            )}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-3 mt-2">
            <Button
              icon={<FaDownload />}
              type="primary"
              disabled={!fileList.some((f) => f.status === "done")}
              loading={zipLoading}
              onClick={async () => {
                setZipLoading(true);
                const zip = new JSZip();
                for (const f of fileList) {
                  if (f.status === "done" && f.compressedBlob) {
                    zip.file(f.compressedName, f.compressedBlob);
                  }
                }
                const content = await zip.generateAsync({ type: "blob" });
                saveAs(content, "converted_webp_images.zip");
                setZipLoading(false);
                message.success("Downloaded ZIP of converted images!");
              }}
              style={{ minWidth: 175 }}>
              Download All as ZIP
            </Button>
            <div className="text-muted" style={{ fontSize: 13 }}>
              <FaInfoCircle style={{ marginRight: 3 }} />
              <b>Settings</b>: adjust quality, max file size, resize.
              <span className="ms-3">
                <FaInfoCircle style={{ marginRight: 3 }} />
                <b>Preview</b>: click any image or eye icon.
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Settings Modal */}
      <Modal
        open={qualityModal}
        title={
          <>
            <FaCog style={{ marginRight: 8 }} />
            Compression Settings
          </>
        }
        onCancel={() => setQualityModal(false)}
        footer={null}
        destroyOnClose>
        <div>
          <div className="d-flex align-items-center mb-3">
            <span style={{ minWidth: 120 }}>Quality (0.5–0.95):</span>
            <InputNumber
              min={0.5}
              max={0.95}
              step={0.05}
              value={desiredQuality}
              onChange={setDesiredQuality}
              style={{ width: 80, marginLeft: 10 }}
              precision={2}
            />
          </div>
          <div className="d-flex align-items-center mb-3">
            <span style={{ minWidth: 120 }}>Max Size (KB):</span>
            <InputNumber
              min={20}
              max={200}
              step={5}
              value={maxSize}
              onChange={setMaxSize}
              style={{ width: 80, marginLeft: 10 }}
              precision={0}
            />
          </div>
          <div className="d-flex align-items-center mb-3">
            <span style={{ minWidth: 120 }}>Max Dimension (px):</span>
            <InputNumber
              min={400}
              max={3000}
              step={100}
              value={resizeMax}
              onChange={setResizeMax}
              style={{ width: 100, marginLeft: 10 }}
              precision={0}
              placeholder="No resize"
            />
            <span className="ms-2 text-secondary small">
              (optional downscale)
            </span>
          </div>
          <Switch
            checked={qualityPerImage}
            onChange={setQualityPerImage}
            style={{ marginRight: 8 }}
          />
          <span>Use custom settings for new uploads</span>
          <div className="mt-3">
            <Button type="primary" onClick={() => setQualityModal(false)}>
              OK
            </Button>
          </div>
        </div>
      </Modal>
      {/* Before/After Preview Modal with swipe compare */}
      <Modal
        open={previewModal}
        title={
          <span style={{ fontWeight: 600, fontSize: 18 }}>
            <FaEye style={{ marginRight: 8 }} />
            Before & After Preview
          </span>
        }
        onCancel={() => setPreviewModal(false)}
        footer={null}
        width={window.innerWidth < 800 ? "98vw" : 750}
        destroyOnClose
        centered>
        {previewRecord && (
          <div>
            <div
              className="mb-3"
              style={{
                background: "#f8f9fa",
                borderRadius: 12,
                padding: "24px 0",
              }}>
              <CompareImage
                leftImage={URL.createObjectURL(previewRecord.file)}
                rightImage={previewRecord.compressedUrl}
                leftImageLabel={
                  <div
                    style={{
                      background: "#1976d2",
                      color: "#fff",
                      padding: "3px 12px",
                      borderRadius: 6,
                      fontSize: 13,
                    }}>
                    Original
                    <br />
                    {(previewRecord.size / 1024).toFixed(1)} KB
                  </div>
                }
                rightImageLabel={
                  <div
                    style={{
                      background: "#43a047",
                      color: "#fff",
                      padding: "3px 12px",
                      borderRadius: 6,
                      fontSize: 13,
                    }}>
                    WebP
                    <br />
                    {(previewRecord.compressedSize / 1024).toFixed(1)} KB
                  </div>
                }
                sliderLineColor="#2196f3"
              />
            </div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">
              <div>
                <Tooltip title="Download original">
                  <Button
                    size="small"
                    icon={<FaDownload />}
                    onClick={() =>
                      saveAs(previewRecord.file, previewRecord.name)
                    }
                    style={{ marginRight: 10 }}>
                    Original
                  </Button>
                </Tooltip>
                <Tooltip title="Download WebP">
                  <Button
                    size="small"
                    icon={<FaDownload />}
                    type="primary"
                    onClick={() =>
                      saveAs(
                        previewRecord.compressedBlob,
                        previewRecord.compressedName
                      )
                    }>
                    WebP
                  </Button>
                </Tooltip>
              </div>
              <div style={{ fontSize: 13, color: "#888" }}>
                Quality:{" "}
                {previewRecord.compressionQuality &&
                  previewRecord.compressionQuality.toFixed(2)}
              </div>
            </div>
          </div>
        )}
      </Modal>
      {/* Responsive Table Styling */}
      <style>{`
        .table-responsive-custom {
          width: 100%;
          overflow-x: auto;
        }
        @media (max-width: 800px) {
          .table-responsive-custom {
            margin: -12px 0 0 -10px;
            padding: 0 0 8px 2px;
            border-radius: 9px;
            background: #fff;
          }
          .ant-table {
            min-width: 580px;
          }
          .ant-table-thead > tr > th,
          .ant-table-tbody > tr > td {
            padding: 7px 6px !important;
            font-size: 13px !important;
          }
        }
        .fa-spin {
          animation: fa-spin 0.9s infinite linear;
        }
        @keyframes fa-spin {
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
